import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DataContext = createContext(null);

const RESOURCES = [
  "tabungan", "budget", "seserahan", "rundown", "vendor",
  "tamu", "life_after_married", "todo", "dokumen", "pembayaran", "rsvp", "guestbook",
  "wa_templates", "moodboard",
];

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [activePasangan, setActivePasangan] = useState(null);
  const [data, setData] = useState({});
  const [pasanganInfo, setPasanganInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  // Determine active pasangan id
  useEffect(() => {
    if (!user) { setActivePasangan(null); return; }
    if (user.role === "sysadmin" || user.role === "Admin") {
      const saved = localStorage.getItem("kh_active_pasangan");
      setActivePasangan(saved || null);
    } else {
      setActivePasangan(user.pasangan_id);
    }
  }, [user]);

  const pid = activePasangan;

  const params = useCallback(() => {
    return (user?.role === "sysadmin" || user?.role === "Admin") && pid ? { pasangan_id: pid } : {};
  }, [user, pid]);

  const reloadAll = useCallback(async () => {
    if (!user) return;
    if ((user.role === "sysadmin" || user.role === "Admin") && !pid) { setData({}); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        RESOURCES.map((r) => api.get(`/data/${r}`, { params: params() }).then((res) => [r, res.data]).catch(() => [r, []]))
      );
      const obj = {};
      results.forEach(([r, d]) => { obj[r] = d; });
      setData(obj);
      if (pid) {
        try {
          const info = await api.get("/pasangan-info", { params: { pasangan_id: pid } });
          setPasanganInfo(info.data);
        } catch { setPasanganInfo(null); }
      }
    } finally {
      setLoading(false);
    }
  }, [user, pid, params]);

  useEffect(() => {
    if (user && (user.role !== "sysadmin" || pid)) {
      reloadAll();
    }
  }, [user, pid, reloadAll]);

  const selectPasangan = (id) => {
    localStorage.setItem("kh_active_pasangan", id);
    setActivePasangan(id);
  };

  const create = async (resource, payload) => {
    const { data: doc } = await api.post(`/data/${resource}`, payload, { params: params() });
    setData((prev) => ({ ...prev, [resource]: [...(prev[resource] || []), doc] }));
    return doc;
  };
  const update = async (resource, id, payload) => {
    const { data: doc } = await api.put(`/data/${resource}/${id}`, payload);
    setData((prev) => ({ ...prev, [resource]: (prev[resource] || []).map((x) => (x.id === id ? doc : x)) }));
    return doc;
  };
  const remove = async (resource, id) => {
    await api.delete(`/data/${resource}/${id}`);
    setData((prev) => ({ ...prev, [resource]: (prev[resource] || []).filter((x) => x.id !== id) }));
  };

  return (
    <DataContext.Provider value={{
      data, loading, activePasangan, selectPasangan, pasanganInfo,
      reloadAll, create, update, remove, hasPasangan: !!pid,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
